// src/components/LearningModules/LearningModules.js
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
// Import the JSON file containing category-level resource partner info
import { HamburgerIcon } from "../../assets/icons/icons";
import { useStrategicSuggestions } from "../../contexts/StrategicSuggestionsContext";
import "./LearningModules.css"; // Your existing CSS
import "./style.css";
import LinksByAudience from "../LinksByAudience";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

// Helper component: ModuleTile
function ModuleTile({ module, showPartners }) {
  const [showResourceLinks, setShowResourceLinks] = useState(false);
  const [expandedAudiences, setExpandedAudiences] = useState({});

  const toggleResourceLinks = () => setShowResourceLinks(v => !v);

  const toggleAudience = (audience) => {
    setExpandedAudiences(prev => ({
      ...prev,
      [audience]: !prev[audience]
    }));
  };

  return (
    <div
      id={module.suggestion_id}
      className="entry-item"
      style={{ marginTop: "10px" }}
    >
      <p className="learning-module-title">{module.title}</p>
      {module.description && (
        <div
          style={{ marginTop: "10px", marginLeft: "20px" }}
          dangerouslySetInnerHTML={{
            __html: module.description && module.description.replace(/^"(.*)"$/, '$1')
          }}
        />
      )}
      {module.links && (
        <div style={{ marginTop: "10px" }}>
          <div
            className="suggestion-toggle-row"
            onClick={toggleResourceLinks}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
              fontWeight: "bold",
              margin: "0.5em 0"
            }}
          >
            <span
              style={{
                display: "inline-block",
                transition: "transform 0.2s",
                transform: showResourceLinks ? "rotate(90deg)" : "rotate(0deg)",
                marginRight: "0.5em"
              }}
            >
              ▶
            </span>
            See Resource Links
          </div>
            {showResourceLinks && (
              <div className="resource-partners-container" style={{ marginLeft: "2em" }}>
                <div className="category-resource-partners">
                  < LinksByAudience linksMap={module.linksMap} />
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

const LearningModules = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { strategicSuggestions } = useStrategicSuggestions();
  const { categories, byId: moduleIds } = strategicSuggestions;
  const [selectedCategory, setSelectedCategory] = useState(categories ? Object.keys(categories)[0] : 0);

  // const [groupedModules, setGroupedModules] = useState({});

  const chooseCategory = (catId) => {
    navigate(`/learning-modules?category=${categories[catId]}`);
  }

  // Helper to retrieve category-level resource partners from JSON
  // const getCategoryResourcePartners = (cat) => {
  //   const categoryInfo = LMCategoryResourcePartners[cat];
  //   return categoryInfo?.resourcePartners || [];
  // };

  // 1️⃣ Group modules & pick initial category based on params
  useEffect(() => {
    if (!searchParams) return;
    const categoryParam = searchParams.get("category");
    const moduleId = searchParams.get("moduleId");

    if (categoryParam) {
       if (categories[categoryParam]) {
         setSelectedCategory(categoryParam);
       } else {
         const catId =Object.keys(categories).find(key => categories[key] === categoryParam);
          if (catId) setSelectedCategory(catId);
       }
    } else if (moduleId) {
      const mod = moduleIds[moduleId];
      if (mod) {
        setSelectedCategory(mod.suggestion_id);
      }
    }

  }, [searchParams, categories, moduleIds]);

  // Set default category once categories load
  useEffect(() => {
    if (categories && Object.keys(categories).length > 0 && !selectedCategory) {
      setSelectedCategory(Object.keys(categories)[0]);
    }
  }, [categories, selectedCategory]);

  if (!categories || Object.keys(categories).length === 0) {
    return <LoadingSpinner message="Loading…" />;
  }

  // 2️⃣ Scroll into view & highlight after category change
  // useEffect(() => {
  //   const moduleId = searchParams.get("moduleId");
  //   if (moduleId) {
  //     setTimeout(() => {
  //       const el = document.getElementById(moduleId);
  //       if (el) {
  //         el.scrollIntoView({ behavior: "smooth", block: "center" });
  //         el.classList.add("highlight");
  //         setTimeout(() => el.classList.remove("highlight"), 3000);
  //       }
  //     }, 300);
  //   }
  // }, [selectedCategory, searchParams]);

  const handleCategorySelect = (e) => {
    chooseCategory(e.target.value);
  };

  const renderModules = () => {
    if (!selectedCategory) {
      // Show all modules if nothing is selected
      const modules = Object.entries(strategicSuggestions.byId).map(([id, suggestion]) => suggestion);
      return (
        <>
          {modules.map((module, idx) => (
            <ModuleTile
              key={module.suggestion_id || idx}
              module={module}
              showPartners={false}
            />
          ))}
        </>
      );
    }

    // Separate modules by selected category
    const modules = Object.entries(strategicSuggestions.byId).map(([id, suggestion]) => suggestion);
    const selectedModules = modules.filter(m => m.suggestion_id === Number(selectedCategory));
    const otherModules = modules.filter(m => m.suggestion_id !== Number(selectedCategory));

    return (
      <>
        {selectedModules.map((module, idx) => (
          <ModuleTile
            key={module.suggestion_id || idx}
            module={module}
            showPartners={true}
          />
        ))}
        {otherModules.map((module, idx) => (
          <ModuleTile
            key={module.suggestion_id || `other-${idx}`}
            module={module}
            showPartners={false}
          />
        ))}
      </>
    );
  };

  return (
    <div className="entries-page">
      {/* LEFT SIDEBAR */}
      <div className="sidebar">
        <h2 className="section-title">Learning Modules</h2>
        <div className="category-list">
          <h3>Categories</h3>
          <div className="category-dropdown-container">
            <div className="select-container">
              <select
                className="category-dropdown"
                value={selectedCategory}
                onChange={handleCategorySelect}
              >
                {Object.entries(categories).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
              <HamburgerIcon className="hamburger-icon" />
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN */}
      <div className="entry-details">
        <>
          <h2>{categories[selectedCategory]}</h2>
          <div style={{ marginTop: "20px" }}>{renderModules()}</div>
        </>
      </div>
    </div>
  );
}

export default LearningModules;
